import React from "react";
import { getAlert, useEscapeClose } from "../../../utils/SamuiUtils";
import api from "../../../utils/axios";

const FileModalLocation = ({
  mode,
  showViewAttachModal,
  setShowViewAttachModal,
  selectedFiles,
  setSelectedFiles,
  Doc_Id,
  docCodeId,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [fileIndexToDelete, setFileIndexToDelete] = React.useState(null);
  const [showFilePreview, setShowFilePreview] = React.useState(false);
  const [currentPreviewFile, setCurrentPreviewFile] = React.useState(null);
  const [loadingFiles, setLoadingFiles] = React.useState(false);
  const [autoOpen, setAutoOpen] = React.useState(false);
  const [hadFilesFromDB, setHadFilesFromDB] = React.useState(false);

  useEscapeClose(showDeleteConfirm, () => setShowDeleteConfirm(false));
  useEscapeClose(showFilePreview, () => setShowFilePreview(false));

  // ⭐ Reset hadFilesFromDB เมื่อปิด modal
  React.useEffect(() => {
    if (!showViewAttachModal) {
      setHadFilesFromDB(false);
    }
  }, [showViewAttachModal]);

  const guessFileType = (fileName) => {
    const ext = (fileName.split(".").pop() || "").toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext))
      return `image/${ext === "jpg" ? "jpeg" : ext}`;
    if (ext === "pdf") return "application/pdf";
    return "application/octet-stream";
  };

  const fetchUrl =
    docCodeId === 6
      ? `/get-quote-file/${Doc_Id}`
      : `/get-contract-file/${Doc_Id}`;

  const deleteUrl = `/delete-quote-file`;

  React.useEffect(() => {
    let mounted = true;

    const loadFiles = async () => {
      if (!showViewAttachModal || !Doc_Id) return;

      try {
        setLoadingFiles(true);
        const res = await api.get(fetchUrl);

        if (mounted && res.data?.status === "OK") {
          const dbFiles = (res.data.result || []).map((f) => ({
            File_Id: f.F_Id,
            File_Name: f.File_Name,
            File_Url: f.File_Url,
            size: f.File_Size || 1024,
            type: guessFileType(f.File_Name),
            isNew: false,
          }));

          setSelectedFiles((prev) => {
            const pending = prev.filter((p) => p.isNew);
            return [...dbFiles, ...pending];
          });

          // ⭐ Track ว่ามีไฟล์จาก DB หรือไม่
          if (dbFiles.length > 0) {
            setHadFilesFromDB(true);
          } else {
            setHadFilesFromDB(false);
          }
        }
      } catch (err) {
        console.error("Load files error:", err);
        getAlert("FAILED", "ไม่สามารถโหลดไฟล์แนบได้");
      } finally {
        if (mounted) setLoadingFiles(false);
      }
    };

    loadFiles();
    return () => (mounted = false);
  }, [showViewAttachModal, Doc_Id, docCodeId]);

  // ⭐ Auto browse เมื่อไม่มีไฟล์ (แต่ไม่เด้งถ้าเคยมีไฟล์จาก DB แล้วลบจนเหลือ 0)
  React.useEffect(() => {
    if (showViewAttachModal && selectedFiles.length === 0 && !hadFilesFromDB) {
      const timer = setTimeout(() => {
        const input = document.getElementById("file-upload-location");
        if (input) {
          setAutoOpen(true);
          input.click();
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [showViewAttachModal, selectedFiles, hadFilesFromDB]);

  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files || []);

    if (autoOpen && files.length === 0) {
      setAutoOpen(false);
      setShowViewAttachModal(false);
      return;
    }

    setAutoOpen(false);

    if (!files.length) return;

    const mapped = files.map((f) => ({
      File_Name: f.name,
      File_Url: URL.createObjectURL(f),
      size: f.size,
      type: f.type || guessFileType(f.name),
      rawFile: f,
      isNew: true,
    }));

    setSelectedFiles((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  const handleConfirmDelete = async () => {
    const f = selectedFiles[fileIndexToDelete];
    if (!f) return;

    if (f.isNew) {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== fileIndexToDelete));
      setShowDeleteConfirm(false);
      return;
    }

    try {
      const res = await api.delete(deleteUrl, { data: { F_Id: f.File_Id } });
      if (res.data?.status === "OK") {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== fileIndexToDelete));
      } else {
        getAlert("FAILED", res.data?.message || "ลบไฟล์ไม่สำเร็จ");
      }
    } catch (err) {
      console.error("Delete error:", err);
      getAlert("FAILED", "ไม่สามารถลบไฟล์ได้");
    }

    setShowDeleteConfirm(false);
  };

  const handlePreviewFile = (file) => {
    setCurrentPreviewFile({
      name: file.File_Name,
      url: file.File_Url,
      type: file.type,
    });
    setShowFilePreview(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const sizes = ["Bytes", "KB", "MB", "GB"];
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(file.File_Url);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.File_Name || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error", err);
      getAlert("FAILED", "ไม่สามารถดาวน์โหลดไฟล์ได้");
    }
  };

  const renderFileGrid = (files) => {
    if (!files.length)
      return (
        <div className="text-center py-5">
          <i className="fa fa-folder-open fa-3x text-muted mb-3"></i>
          <p className="text-secondary">ไม่มีไฟล์แนบ</p>
        </div>
      );

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: "20px",
        }}
      >
        {files.map((file, index) => {
          const ext = file.File_Name?.split(".").pop()?.toLowerCase() || "";
          const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);

          return (
            <div
              key={file.File_Id || index}
              className="border rounded-lg shadow-sm text-center p-3 position-relative"
            >
              {mode !== "S" && (
                <button
                  onClick={() => {
                    setFileIndexToDelete(index);
                    setShowDeleteConfirm(true);
                  }}
                  className="btn btn-sm btn-danger position-absolute top-0 start-0 m-2 rounded-circle"
                >
                  <i className="fa fa-times"></i>
                </button>
              )}

              <div
                onClick={() => handlePreviewFile(file)}
                style={{
                  height: "90px",
                  backgroundColor: "#f9fafb",
                  border: "1px solid #eee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                {isImage ? (
                  <img
                    src={file.File_Url}
                    alt={file.File_Name}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <i className="fa fa-file" style={{ fontSize: "36px", color: "#6c757d" }}></i>
                )}
              </div>

              <div className="fw-semibold small text-truncate mt-2" title={file.File_Name}>
                {file.File_Name}
              </div>
              <div className="text-muted small mb-2">{formatFileSize(file.size)}</div>

              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-sm"
                  style={{
                    backgroundColor: "rgb(239, 108, 0)",
                    color: "white",
                    padding: "4px 8px",
                    fontSize: "11px",
                    borderRadius: "6px",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onClick={() => handlePreviewFile(file)}
                >
                  <i className="fa fa-eye" style={{ fontSize: "11px" }}></i>
                  <span style={{ fontSize: "11px" }}> ดู</span>
                </button>

                {!file.isNew && (
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      padding: "4px 8px",
                      fontSize: "11px",
                      borderRadius: "6px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onClick={() => handleDownload(file)}
                  >
                    <i className="fa fa-download" style={{ fontSize: "11px" }}></i>
                    <span style={{ fontSize: "11px" }}> โหลด</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPreview = () => {
    if (!currentPreviewFile) return null;

    if (currentPreviewFile.type?.startsWith("image/"))
      return (
        <img
          src={currentPreviewFile.url}
          alt={currentPreviewFile.name}
          className="img-fluid"
        />
      );

    if (currentPreviewFile.type === "application/pdf")
      return (
        <iframe
          src={currentPreviewFile.url}
          title={currentPreviewFile.name}
          width="100%"
          height="600px"
        ></iframe>
      );

    return (
      <div className="text-center py-5">
        <i className="fa fa-file fa-4x text-secondary mb-3"></i>
        <p>ไม่สามารถแสดงตัวอย่างไฟล์นี้ได้</p>
        <a
          className="btn btn-primary"
          href={currentPreviewFile.url}
          target="_blank"
          rel="noreferrer"
        >
          เปิดไฟล์
        </a>
      </div>
    );
  };

  return (
    <div
      className={`modal ${showViewAttachModal ? "show" : ""}`}
      style={{ display: showViewAttachModal ? "block" : "none" }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title">
              <i className="fa fa-paperclip me-2"></i>
              {docCodeId === 20 ? "ไฟล์แนบสัญญา" : "ไฟล์แนบใบเสนอราคา"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowViewAttachModal(false)}
            ></button>
          </div>

          <div className="modal-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="mb-0 fw-semibold">
                <i className="fa fa-folder me-2"></i>เอกสารแนบ
              </h6>

              {mode !== "S" && (
                <>
                  <input
                    type="file"
                    multiple
                    id="file-upload-location"
                    style={{ display: "none" }}
                    onChange={handleFileSelection}
                  />
                  <button
                    className="btn text-white"
                    style={{
                      backgroundColor: "rgb(239,108,0)",
                      borderRadius: "8px",
                      padding: "10px 20px",
                    }}
                    onClick={() =>
                      document.getElementById("file-upload-location").click()
                    }
                  >
                    <i className="fa fa-plus me-2"></i>แนบไฟล์
                  </button>
                </>
              )}
            </div>

            <div style={{ maxHeight: "600px", overflowY: "auto" }}>
              {loadingFiles ? (
                <div className="text-center py-5 text-muted">
                  <i className="fa fa-spinner fa-spin me-2"></i>กำลังโหลดไฟล์...
                </div>
              ) : (
                renderFileGrid(selectedFiles)
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showFilePreview ? "show" : ""}`}
        style={{
          display: showFilePreview ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.8)",
          zIndex: 1060,
        }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-dark text-white border-0">
              <h5 className="modal-title">
                <i className="fa fa-eye me-2"></i>ตัวอย่างไฟล์:{" "}
                {currentPreviewFile?.name}
              </h5>
              <button
                className="btn-close btn-close-white"
                onClick={() => setShowFilePreview(false)}
              ></button>
            </div>
            <div
              className="modal-body text-center"
              style={{
                backgroundColor: "#000000 !important",
                padding: 0,
                minHeight: "400px",
              }}
            >
              {renderPreview()}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showDeleteConfirm ? "show" : ""}`}
        style={{
          display: showDeleteConfirm ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content text-center">
            <div className="modal-header">
              <h5 className="modal-title fw-bold text-danger">
                <i className="fa fa-exclamation-triangle me-2"></i>ยืนยันการลบไฟล์
              </h5>
            </div>
            <div className="modal-body">
              <p>คุณแน่ใจหรือไม่ที่จะลบไฟล์นี้?</p>
              <p className="text-muted small">
                เมื่อลบแล้วจะไม่สามารถกู้คืนได้
              </p>
              <div className="d-flex justify-content-center gap-3 mt-3">
                <button
                  className="btn btn-danger px-4"
                  onClick={handleConfirmDelete}
                >
                  <i className="fa fa-check me-2"></i>ยืนยัน
                </button>
                <button
                  className="btn btn-outline-secondary px-4 text-dark fw-semibold"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #ced4da",
                  }}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <i className="fa fa-times me-2"></i>ยกเลิก
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileModalLocation;
